import React from 'react';
import {
  CapabilityDecision,
  CAPABILITY_DOMAIN_LABELS,
} from './types';

interface CapabilitySummaryProps {
  decisions: CapabilityDecision[];
  onConfirm: () => void;
  onCancel: () => void;
  isCreating: boolean;
}

/**
 * CapabilitySummary - Overview of capability assessment
 * 
 * Shows skills overview, requires explicit confirmation to save.
 * Mijlpalen worden gegenereerd tijdens sparring met volledige context.
 */
const CapabilitySummary: React.FC<CapabilitySummaryProps> = ({
  decisions,
  onConfirm,
  onCancel,
  isCreating,
}) => {

  // Helper: Get skill bar configuration per decision
  const getSkillBarConfig = (decision: CapabilityDecision) => {
    if (!decision.required) {
      return { type: 'badge' as const, text: 'Niet van toepassing', color: 'gray' };
    }
    
    if (decision.responsibility === 'other') {
      return { type: 'badge' as const, text: 'Wordt georganiseerd', color: 'blue' };
    }
    
    if (decision.responsibility === 'self') {
      const levelMap = {
        'basic': { width: 33, label: 'Basis' },
        'workable': { width: 67, label: 'Werkbaar' },
        'advanced': { width: 100, label: 'Gevorderd' },
      };
      
      const config = levelMap[decision.level!];
      const action = decision.resolution 
        ? ` → ${decision.resolution === 'learn' ? 'Leren' 
            : decision.resolution === 'outsource' ? 'Uitbesteden' 
            : 'Aannemen'}`
        : '';
      
      return { 
        type: 'bar' as const, 
        width: config.width, 
        label: `${config.label} (zelf)${action}`,
      };
    }
    
    return { type: 'badge' as const, text: 'Onbekend', color: 'gray' };
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
      {/* Skill overview with bar chart */}
      <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900">Je capaciteiten overzicht</h4>
        
        <div className="space-y-3">
          {decisions.map((decision) => {
            const config = getSkillBarConfig(decision);
            const domain = CAPABILITY_DOMAIN_LABELS[decision.domain];
            
            return (
              <div key={decision.domain} className="space-y-1.5">
                <div className="text-sm font-medium text-gray-700">{domain}</div>
                
                {config.type === 'bar' ? (
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gray-300 transition-all"
                        style={{ width: `${config.width}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-600 w-52 flex-shrink-0">{config.label}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 h-2 bg-gray-100 rounded-full" />
                    <span className="text-xs w-52 flex-shrink-0">
                      <span className={`px-2 py-0.5 rounded ${
                        config.color === 'blue' 
                          ? 'bg-blue-50 border border-blue-200 text-blue-700'
                          : 'bg-gray-100 border border-gray-200 text-gray-600'
                      }`}>
                        {config.text}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Explanation */}
      <div className="pt-4 border-t border-gray-200">
        <p className="text-sm text-gray-600">
          Deze informatie wordt gebruikt tijdens de sparring om gerichte vragen te stellen.
          Mijlpalen worden gegenereerd na de sparring met volledige context.
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-4">
        <button
          onClick={onCancel}
          disabled={isCreating}
          className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
        >
          Annuleren
        </button>
        <button
          onClick={onConfirm}
          disabled={isCreating}
          className="
            px-6 py-2 bg-gray-900 text-white rounded-lg
            hover:bg-gray-800 transition-colors
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          {isCreating ? 'Opslaan...' : 'Doorgaan naar sparring'}
        </button>
      </div>
    </div>
  );
};

export default CapabilitySummary;
